import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, LogOut, ScrollText, Sparkles, Trash2, HandHeart, MessageCircle, ShieldOff } from "lucide-react";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Navbar() {
  const { user, setUser, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const handleDeleted = () => {
    setUser(null);
    navigate("/", { replace: true });
  };

  const handleLogoutAll = async () => {
    setSigningOutAll(true);
    try {
      await api.post("/auth/logout-all");
      toast.success("Signed out on all devices");
      setUser(null);
      navigate("/", { replace: true });
    } catch {
      toast.error("Couldn't sign out everywhere — please try again");
    } finally {
      setSigningOutAll(false);
      setLogoutAllOpen(false);
    }
  };

  return (
    <header className="relative z-10 border-b border-[#EFEBF4] bg-[#FDFBF7]/85 backdrop-blur-xl">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/dashboard" data-testid="navbar-brand" className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: "#9B8BAF" }}>
            <Heart className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
          </div>
          <span className="text-base tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            My Daily Prayer
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-sm">
          <NavLink to="/dashboard" active={pathname === "/dashboard"} testId="nav-active">
            <ScrollText className="h-4 w-4" /> Active
          </NavLink>
          <NavLink to="/answered" active={pathname === "/answered"} testId="nav-answered">
            <Sparkles className="h-4 w-4" /> Answered
          </NavLink>
        </nav>

        <DropdownMenu>
          <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
            <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-[#EFEBF4] transition-colors">
              <Avatar className="h-8 w-8 border border-[#D4C7E1]">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#EFEBF4] text-[#9B8BAF] text-xs">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline text-sm text-[#2C2A2E] max-w-[140px] truncate">
                {user?.name}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-white border border-[#D4C7E1]">
            <DropdownMenuLabel className="text-[#75737A] text-xs uppercase tracking-[0.15em]">
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/answered")} data-testid="menu-answered">
              <Sparkles className="h-4 w-4 mr-2" /> Answered prayers
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/support")} data-testid="menu-support">
              <HandHeart className="h-4 w-4 mr-2" /> Support &amp; give
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/contact")} data-testid="menu-contact">
              <MessageCircle className="h-4 w-4 mr-2" /> Contact us
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-[#C28A8A]">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setLogoutAllOpen(true)}
              data-testid="menu-logout-all"
              className="text-[#C28A8A]"
            >
              <ShieldOff className="h-4 w-4 mr-2" /> Sign out everywhere
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setDeleteOpen(true)}
              data-testid="menu-delete-account"
              className="text-[#C28A8A]"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete account
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile tabs */}
      <div className="md:hidden border-t border-[#EFEBF4] flex">
        <MobileTab to="/dashboard" active={pathname === "/dashboard"} label="Active" />
        <MobileTab to="/answered" active={pathname === "/answered"} label="Answered" />
      </div>

      <DeleteAccountDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        userEmail={user?.email}
        onDeleted={handleDeleted}
      />

      <ConfirmDialog
        open={logoutAllOpen}
        onOpenChange={setLogoutAllOpen}
        title="Sign out everywhere?"
        description="This will end your session on every device you've ever signed in on. You'll need to sign in again."
        confirmLabel="Sign out everywhere"
        cancelLabel="Stay signed in"
        destructive
        loading={signingOutAll}
        onConfirm={handleLogoutAll}
      />
    </header>
  );
}

function NavLink({ to, active, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors ${
        active ? "bg-[#EFEBF4] text-[#2C2A2E]" : "text-[#75737A] hover:text-[#2C2A2E]"
      }`}
    >
      {children}
    </Link>
  );
}

function MobileTab({ to, active, label }) {
  return (
    <Link
      to={to}
      className={`flex-1 text-center py-3 text-sm ${
        active ? "text-[#2C2A2E] border-b-2 border-[#9B8BAF]" : "text-[#75737A]"
      }`}
    >
      {label}
    </Link>
  );
}
